import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { RichTextEditor } from '../components/RichTextEditor';
import { PRIMARY, TEXT_MUTED, BORDER_LIGHT } from '../components/legacy-ball/table-management/designTokens';
import { EventGroupsPanel } from '../components/event-groups/EventGroupsPanel';
import { SwipeableNotificationCard } from '../components/SwipeableNotificationCard';
import { useMediaQuery } from '../hooks/useMediaQuery';

interface Announcement {
  id: string;
  title: string;
  body: string;
  status: 'Draft' | 'Published';
  audience: 'All' | 'DelegatesOnly' | 'AttendeesOnly' | 'EventRegistrants' | 'EventGroup';
  eventGroupId?: string;
  deliveredAt?: string;
  createdAt: string;
}

interface EventGroup {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface Notification {
  id: string;
  notificationId: string;
  read: boolean;
  readAt?: string;
  createdAt: string;
  title: string;
  body: string;
  deliveredAt?: string;
  dismissed?: boolean;
  dismissedAt?: string;
}

type SubView = 'announcements' | 'inbox' | 'groups';

const SUB_VIEWS: { key: SubView; label: string; minRole: string }[] = [
  { key: 'announcements', label: 'Announcements', minRole: 'Organizer' },
  { key: 'inbox', label: 'My Inbox', minRole: 'Organizer' },
  { key: 'groups', label: 'Groups', minRole: 'Organizer' },
];

/** EventGroupsPanel is imported from components/event-groups/ */

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  tabBar: { display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: `2px solid ${BORDER_LIGHT}` },
  tab: { padding: '0.6rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: TEXT_MUTED, borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { padding: '0.6rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: PRIMARY, borderBottom: `2px solid ${PRIMARY}`, marginBottom: '-2px' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.4rem 0.75rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  select: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, minHeight: '100px', resize: 'vertical' as const },
  card: { padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.75rem', backgroundColor: '#fafafa' },
  badge: { display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, marginLeft: '0.5rem' },
  unreadDot: { display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#c9a84c', marginRight: '0.5rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
};

/** My Inbox section — personal notifications with dismiss/delete */
function MyInboxSection({ notifications, setNotifications, error, setError }: {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  error: string;
  setError: (e: string) => void;
}) {
  const activeNotifs = notifications.filter(n => !n.dismissed);
  const dismissedNotifs = notifications.filter(n => n.dismissed);
  const [showDismissed, setShowDismissed] = useState(false);
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const isMobile = useMediaQuery(768);

  // Outside-tap-to-close: close open card when tapping outside the action panel
  useEffect(() => {
    if (!openCardId) return;

    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      // If the click target is inside an action panel, ignore it
      const actionPanel = target.closest('[role="group"][aria-label="Notification actions"]');
      if (actionPanel) return;
      // Otherwise close
      setOpenCardId(null);
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openCardId]);

  async function handleMarkRead(notificationId: string, announcementId: string) {
    try {
      await apiClient(`/announcements/${announcementId}/read`, { method: 'PATCH' });
      setNotifications(prev => prev.map(n => (n.notificationId || n.id) === notificationId ? { ...n, read: true } : n));
    } catch { /* non-critical */ }
  }

  async function handleDismiss(announcementId: string) {
    try {
      await apiClient(`/announcements/${announcementId}/dismiss`, { method: 'PATCH' });
      setNotifications(prev => prev.map(n => n.id === announcementId ? { ...n, dismissed: true, read: true } : n));
    } catch (err: any) { setError(err?.message || 'Failed to dismiss.'); }
  }

  async function handleDelete(announcementId: string) {
    if (!confirm('Remove this notification from your inbox?')) return;
    try {
      await apiClient(`/announcements/${announcementId}/delete-notification`, { method: 'PATCH' });
      setNotifications(prev => prev.filter(n => n.id !== announcementId));
    } catch (err: any) { setError(err?.message || 'Failed to delete.'); }
  }

  return (
    <>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>My Notifications</h2>
        {activeNotifs.length === 0 ? (
          <p style={{ color: '#666' }}>No notifications.</p>
        ) : (
          activeNotifs.map((notif) => (
            <SwipeableNotificationCard
              key={notif.notificationId || notif.id}
              notification={notif}
              onDismiss={handleDismiss}
              onDelete={handleDelete}
              openCardId={openCardId}
              onSwipeStart={(id) => setOpenCardId(id)}
              swipeEnabled={isMobile}
            >
              <div
                style={{ ...styles.card, backgroundColor: notif.read ? '#fafafa' : '#fff', borderLeft: notif.read ? '4px solid #e0e0e0' : '4px solid #c9a84c' }}
                onClick={() => { if (!notif.read) handleMarkRead(notif.notificationId || notif.id, notif.id); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {!notif.read && <span style={styles.unreadDot} />}
                    <strong style={{ color: '#0f2d1f' }}>{notif.title}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.8rem', color: '#666' }}
                      onClick={(e) => { e.stopPropagation(); handleDismiss(notif.id); }}
                      title="Dismiss (personal)"
                    >↓ Dismiss</button>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.8rem', color: '#d32f2f' }}
                      onClick={(e) => { e.stopPropagation(); handleDelete(notif.id); }}
                      title="Remove from my inbox"
                    >✕</button>
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#444', margin: '0.5rem 0 0' }} dangerouslySetInnerHTML={{ __html: notif.body }} />
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' }}>
                  {new Date(notif.createdAt).toLocaleDateString()}
                  {notif.read && ' · Read'}
                </div>
              </div>
            </SwipeableNotificationCard>
          ))
        )}
      </div>

      {dismissedNotifs.length > 0 && (
        <div style={{ ...styles.section, opacity: 0.8 }}>
          <button
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%' }}
            onClick={() => setShowDismissed(!showDismissed)}
            aria-expanded={showDismissed}
          >
            <h2 style={{ ...styles.sectionTitle, margin: 0, flex: 1, textAlign: 'left' }}>Dismissed ({dismissedNotifs.length})</h2>
            <span style={{ fontSize: '1rem', color: '#666', transform: showDismissed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
          </button>
          {showDismissed && (
            <div style={{ marginTop: '0.75rem' }}>
              {dismissedNotifs.map((notif) => (
                <div key={notif.notificationId || notif.id} style={{ ...styles.card, backgroundColor: '#f5f5f5', borderLeft: '4px solid #ccc', opacity: 0.7 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#555', fontSize: '0.85rem' }}>{notif.title}</strong>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.8rem', color: '#d32f2f' }}
                      onClick={() => handleDelete(notif.id)}
                      title="Delete permanently"
                    >🗑️</button>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', margin: '0.4rem 0 0' }} dangerouslySetInnerHTML={{ __html: notif.body }} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function AnnouncementsPage() {
  const { user } = useAuth();
  const isOrganizer = isAtLeast(user?.role || '', 'Organizer');
  // Only check page authorization for organizers (admin panel).
  // Non-organizers always have access to their personal notification inbox.
  const { canEdit } = usePageAuthorization(isOrganizer ? 'announcements' : '__always_view__');

  const [activeView, setActiveView] = useState<SubView>('announcements');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showCompose, setShowCompose] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formAudience, setFormAudience] = useState<string>('All');
  const [selectedEventGroupId, setSelectedEventGroupId] = useState<string>('');
  const [eventGroups, setEventGroups] = useState<EventGroup[]>([]);
  const [formSendSms, setFormSendSms] = useState(false);
  const [error, setError] = useState('');

  async function fetchData() {
    try {
      if (isOrganizer) {
        const data = await apiClient<Announcement[]>('/announcements');
        setAnnouncements(data);
        // Also fetch personal inbox for My Inbox tab
        const inbox = await apiClient<Notification[]>('/announcements/my-inbox');
        setNotifications(inbox);
      } else {
        const data = await apiClient<Notification[]>('/announcements');
        setNotifications(data);
      }
    } catch {
      setError('Failed to load announcements.');
    }
  }

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (formAudience === 'EventGroup') {
      apiClient<EventGroup[]>('/event-groups')
        .then((groups) => setEventGroups(groups.filter((g) => g.isActive)))
        .catch(() => setEventGroups([]));
    }
  }, [formAudience]);

  async function handleCompose(e: FormEvent) {
    e.preventDefault();
    if (!formTitle || !formBody) { setError('Title and body are required.'); return; }
    if (formAudience === 'EventGroup' && !selectedEventGroupId) {
      setError('Please select an Event Group.');
      return;
    }
    try {
      const body: Record<string, string | boolean> = { title: formTitle, body: formBody, audience: formAudience, sendSms: formSendSms };
      if (formAudience === 'EventGroup') {
        body.eventGroupId = selectedEventGroupId;
      }
      await apiClient('/announcements', {
        method: 'POST',
        body,
      });
      setFormTitle(''); setFormBody(''); setFormAudience('All'); setSelectedEventGroupId(''); setFormSendSms(false);
      setShowCompose(false);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create announcement.');
    }
  }

  async function handlePublish(id: string) {
    try {
      await apiClient(`/announcements/${id}/publish`, { method: 'POST' });
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to publish announcement.');
    }
  }

  async function handleMarkRead(notificationId: string, announcementId: string) {
    try {
      await apiClient(`/announcements/${announcementId}/read`, { method: 'PATCH' });
      setNotifications((prev) =>
        prev.map((n) => n.id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      );
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to mark notification as read.');
    }
  }

  // Organizer View
  if (isOrganizer) {
    const visibleTabs = SUB_VIEWS.filter(v => isAtLeast(user?.role || '', v.minRole));

    return (
      <div style={styles.page}>
        <h1 style={styles.title}>
          Announcements
          <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6b7280', marginLeft: '0.5rem' }}>
            ({announcements.length} total)
          </span>
        </h1>

        {/* Sub-tab Navigation */}
        <nav style={styles.tabBar} aria-label="Announcements section views">
          {visibleTabs.map((view) => (
            <button
              key={view.key}
              style={activeView === view.key ? styles.tabActive : styles.tab}
              onClick={() => setActiveView(view.key)}
              aria-current={activeView === view.key ? 'page' : undefined}
            >
              {view.label}
            </button>
          ))}
        </nav>

        {/* Announcements Tab */}
        {activeView === 'announcements' && (
          <>
            {error && <div style={styles.error}>{error}</div>}

            <div style={{ marginBottom: '1.5rem' }}>
              {canEdit && (
              <button style={styles.btn} onClick={() => setShowCompose(!showCompose)}>
                {showCompose ? 'Cancel' : '+ Compose Announcement'}
              </button>
              )}
            </div>

            {showCompose && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>New Announcement</h2>
                <form onSubmit={handleCompose}>
                  <div style={styles.field}>
                    <label style={styles.label}>Title</label>
                    <input style={styles.input} value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Body</label>
                    <RichTextEditor value={formBody} onChange={setFormBody} placeholder="Write your announcement..." />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Audience</label>
                    <select style={styles.select} value={formAudience} onChange={(e) => { setFormAudience(e.target.value); setSelectedEventGroupId(''); }}>
                      <option value="All">All Users</option>
                      <option value="DelegatesOnly">Delegates Only</option>
                      <option value="AttendeesOnly">Attendees Only</option>
                      <option value="EventRegistrants">Event Registrants</option>
                      <option value="EventGroup">Event Group</option>
                    </select>
                  </div>
                  {formAudience === 'EventGroup' && (
                    <div style={styles.field}>
                      <label style={styles.label}>Select Group</label>
                      <select
                        style={styles.select}
                        value={selectedEventGroupId}
                        onChange={(e) => setSelectedEventGroupId(e.target.value)}
                      >
                        <option value="">-- Select an Event Group --</option>
                        {eventGroups.map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.75rem 0' }}>
                    <input
                      type="checkbox"
                      id="sendSmsCheckbox"
                      checked={formSendSms}
                      onChange={(e) => setFormSendSms(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="sendSmsCheckbox" style={{ fontSize: '0.85rem', color: '#333', cursor: 'pointer' }}>
                      📱 Also send as SMS to recipients with phone numbers
                    </label>
                  </div>
                  <button type="submit" style={styles.btn}>Save Draft</button>
                </form>
              </div>
            )}

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>All Announcements</h2>
              {announcements.length === 0 ? (
                <p style={{ color: '#666' }}>No announcements yet.</p>
              ) : (
                announcements.map((ann) => (
                  <div key={ann.id} style={styles.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong style={{ color: '#0f2d1f' }}>{ann.title}</strong>
                        <span style={{ ...styles.badge, backgroundColor: ann.status === 'Published' ? '#1a6b3c' : '#666', color: '#fff' }}>
                          {ann.status}
                        </span>
                        <span style={{ ...styles.badge, backgroundColor: '#f5f5f5', color: '#666' }}>
                          {ann.audience}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {canEdit && ann.status === 'Draft' && (
                          <button style={styles.btnGold} onClick={() => handlePublish(ann.id)}>Publish</button>
                        )}
                        {canEdit && (
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.85rem', color: '#d32f2f' }}
                            onClick={async () => {
                              if (!confirm(`Delete "${ann.title}"? This removes it for all recipients.`)) return;
                              try {
                                await apiClient(`/announcements/${ann.id}`, { method: 'DELETE' });
                                setAnnouncements(prev => prev.filter(a => a.id !== ann.id));
                              } catch (err: any) {
                                setError(err?.message || 'Failed to delete announcement.');
                              }
                            }}
                            title="Delete announcement"
                            aria-label={`Delete ${ann.title}`}
                          >🗑️</button>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#444', margin: '0.5rem 0 0' }} dangerouslySetInnerHTML={{ __html: ann.body }} />
                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' }}>
                      Created: {new Date(ann.createdAt).toLocaleDateString()}
                      {ann.deliveredAt && ` · Delivered: ${new Date(ann.deliveredAt).toLocaleDateString()}`}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* My Inbox Tab — personal notifications for this organizer/admin */}
        {activeView === 'inbox' && (
          <MyInboxSection
            notifications={notifications}
            setNotifications={setNotifications}
            error={error}
            setError={setError}
          />
        )}

        {/* Groups Tab */}
        {activeView === 'groups' && <EventGroupsPanel />}
      </div>
    );
  }

  // Non-Organizer View (Notification Inbox)
  const activeNotifs = notifications.filter(n => !n.dismissed);
  const dismissedNotifs = notifications.filter(n => n.dismissed);
  const [showDismissed, setShowDismissed] = useState(false);
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const isMobile = useMediaQuery(768);

  // Outside-tap-to-close: close open card when tapping outside the action panel
  useEffect(() => {
    if (!openCardId) return;

    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      // If the click target is inside an action panel, ignore it
      const actionPanel = target.closest('[role="group"][aria-label="Notification actions"]');
      if (actionPanel) return;
      // Otherwise close
      setOpenCardId(null);
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openCardId]);

  async function handleDismiss(announcementId: string) {
    try {
      await apiClient(`/announcements/${announcementId}/dismiss`, { method: 'PATCH' });
      setNotifications(prev => prev.map(n => n.id === announcementId ? { ...n, dismissed: true, read: true } : n));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to dismiss notification.');
    }
  }

  async function handleDelete(announcementId: string) {
    if (!confirm('Permanently delete this notification?')) return;
    try {
      await apiClient(`/announcements/${announcementId}/delete-notification`, { method: 'PATCH' });
      setNotifications(prev => prev.filter(n => n.id !== announcementId));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete notification.');
    }
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>
        Notifications
        <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6b7280', marginLeft: '0.5rem' }}>
          ({activeNotifs.length}{dismissedNotifs.length > 0 ? ` active, ${dismissedNotifs.length} dismissed` : ''})
        </span>
      </h1>
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Inbox</h2>
        {activeNotifs.length === 0 ? (
          <p style={{ color: '#666' }}>No notifications.</p>
        ) : (
          activeNotifs.map((notif) => (
            <SwipeableNotificationCard
              key={notif.notificationId || notif.id}
              notification={notif}
              onDismiss={handleDismiss}
              onDelete={handleDelete}
              openCardId={openCardId}
              onSwipeStart={(id) => setOpenCardId(id)}
              swipeEnabled={isMobile}
            >
              <div
                style={{ ...styles.card, backgroundColor: notif.read ? '#fafafa' : '#fff', borderLeft: notif.read ? '4px solid #e0e0e0' : '4px solid #c9a84c' }}
                onClick={() => { if (!notif.read) handleMarkRead(notif.notificationId || notif.id, notif.id); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {!notif.read && <span style={styles.unreadDot} />}
                    <strong style={{ color: '#0f2d1f' }}>{notif.title}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.8rem', borderRadius: '4px', color: '#666' }}
                      onClick={(e) => { e.stopPropagation(); handleDismiss(notif.id); }}
                      title="Dismiss"
                      aria-label="Dismiss notification"
                    >↓ Dismiss</button>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.8rem', borderRadius: '4px', color: '#d32f2f' }}
                      onClick={(e) => { e.stopPropagation(); handleDelete(notif.id); }}
                      title="Delete"
                      aria-label="Delete notification"
                    >🗑️</button>
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#444', margin: '0.5rem 0 0' }} dangerouslySetInnerHTML={{ __html: notif.body }} />
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' }}>
                  {new Date(notif.createdAt).toLocaleDateString()}
                  {notif.read && ' · Read'}
                </div>
              </div>
            </SwipeableNotificationCard>
          ))
        )}
      </div>

      {/* Dismissed Section — collapsible, default collapsed */}
      {dismissedNotifs.length > 0 && (
        <div style={{ ...styles.section, opacity: 0.8 }}>
          <button
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%' }}
            onClick={() => setShowDismissed(!showDismissed)}
            aria-expanded={showDismissed}
          >
            <h2 style={{ ...styles.sectionTitle, margin: 0, flex: 1, textAlign: 'left' }}>
              Dismissed ({dismissedNotifs.length})
            </h2>
            <span style={{ fontSize: '1rem', color: '#666', transition: 'transform 0.2s', transform: showDismissed ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
          </button>
          {showDismissed && (
            <div style={{ marginTop: '0.75rem' }}>
              {dismissedNotifs.map((notif) => (
                <div
                  key={notif.notificationId || notif.id}
                  style={{ ...styles.card, backgroundColor: '#f5f5f5', borderLeft: '4px solid #ccc', opacity: 0.7 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <strong style={{ color: '#555', fontSize: '0.85rem' }}>{notif.title}</strong>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.8rem', color: '#d32f2f' }}
                      onClick={() => handleDelete(notif.id)}
                      title="Delete permanently"
                      aria-label="Delete notification permanently"
                    >🗑️</button>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', margin: '0.4rem 0 0' }} dangerouslySetInnerHTML={{ __html: notif.body }} />
                  <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '0.4rem' }}>
                    {new Date(notif.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
